import { PLACEHOLDER_IMAGE_PATH } from '../../components/ImageStuff/GeneratedImage';
import { useChatContext } from '../../context/ChatContextProvider';
import { FakeImage } from '../../types';
import { useState, useEffect } from 'react';

interface ChatImageState {
  currentImageUrl: string | undefined;
  currentImageFullObject: FakeImage | undefined;
  currentIndex: number;
  allImages: FakeImage[];
  botName: string;
  navigateToImage: (direction: 'next' | 'prev') => void;
  setCurrentImage: (imageUrl: string) => void;
  hasNextImage: boolean;
  hasPrevImage: boolean;
  addImageToActiveChat: (fakeImage: FakeImage) => void;
}

const useChatHelpers = (): ChatImageState => {
  const { chats, activeChatId, updateChat } = useChatContext();
  const activeChat = chats.find((chat) => chat.id === activeChatId);
  
  const currentImageUrl = activeChat?.activeImage;
  const allImages = activeChat?.fakeImageObjects || [];
  const currentIndex = allImages.findIndex(img => img.imageUrl === currentImageUrl);
  const botName = activeChat?.botName || '';

  const hasNextImage = currentIndex < allImages.length - 1;
  const hasPrevImage = currentIndex > 0;

  const setCurrentImage = (imageUrl: string) => {
    if (!activeChatId) return;
    updateChat(activeChatId, { activeImage: imageUrl });
  };

  const navigateToImage = (direction: 'next' | 'prev') => {
    if (!activeChat) return;
    
    const newIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
    if (newIndex >= 0 && newIndex < allImages.length) {
      setCurrentImage(allImages[newIndex].imageUrl);
    }
  };

  const currentImageFullObject = allImages.find(img => img.imageUrl === currentImageUrl);

  const addImageToActiveChat = (fakeImage: FakeImage) => {
    if (!activeChatId) return;
    updateChat(activeChatId, { fakeImageObjects: [...allImages, fakeImage] });
  };

  return {
    currentImageUrl,
    currentImageFullObject,
    currentIndex,
    allImages,
    botName,
    navigateToImage,
    setCurrentImage,
    hasNextImage,
    hasPrevImage,
    addImageToActiveChat,
  };
};

export default useChatHelpers;

export const useImageRetry = (imageUrl: string | undefined) => {
  const [retryCount, setRetryCount] = useState(0);
  const [shouldRetry, setShouldRetry] = useState(false);

  useEffect(() => {
    if (shouldRetry) {
      const timer = setTimeout(() => {
        setShouldRetry(false);
        setRetryCount(count => count + 1);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [shouldRetry]);

  const handleImageLoadError = () => {
    if (retryCount < 30) {
      setShouldRetry(true);
    }
  };

  const retryEnabledImageUrl = imageUrl 
    ? shouldRetry 
      ? PLACEHOLDER_IMAGE_PATH
      : `${imageUrl}?retry=${retryCount}` 
    : undefined;

  return {
    handleImageLoadError,
    retryEnabledImageUrl,
  };
};